import { useState, useEffect } from 'react'
import { useTaxApi } from './use-tax-api'
import { ProvincialTaxVersionResponse } from './use-tax-api'

interface UseTaxDataOptions {
  subdivisionId?: number
  enabled?: boolean
}

interface UseTaxDataResult {
  taxData: ProvincialTaxVersionResponse | null;
  taxDataLoading: boolean;
  taxDataError: string | null;
}

/**
 * Custom hook to fetch tax data using the new hook-based API
 * Updated to use useTaxApi instead of class-based TaxApiService
 * Now automatically uses the auth token from the useApi hook
 */
export function useTaxData({
  subdivisionId,
  enabled = true
}: UseTaxDataOptions): UseTaxDataResult {
  const [taxData, setTaxData] = useState<ProvincialTaxVersionResponse | null>(null)
  const [taxDataLoading, setTaxDataLoading] = useState(false)
  const [taxDataError, setTaxDataError] = useState<string | null>(null)
  
  const taxApi = useTaxApi()

  useEffect(() => {
    if (!enabled || !subdivisionId) {
      setTaxData(null)
      setTaxDataLoading(false)
      setTaxDataError(null)
      
      if (!enabled) {
        console.log('Tax data fetching disabled')
      } else if (!subdivisionId) {
        console.log('No subdivision ID provided for tax data')
      }
      
      return
    }

    const fetchTaxData = async () => {
      setTaxDataLoading(true)
      setTaxDataError(null)

      try {
        console.log(`Fetching tax data for subdivision ID: ${subdivisionId}`)
        
        const response = await taxApi.getActiveCurrentlyEffectiveProvincialTaxVersionBySubdivisionId(subdivisionId)
        
        console.log('Tax data fetched successfully:', {
          subdivisionId,
          effectiveDate: response.EffectiveDate,
          hasCPP: !!response.TaxTypeCanadaPensionPlan,
          hasCPP2: !!response.TaxTypeCanadaPensionPlan2,
          hasQPP: !!response.TaxTypeQuebecPensionPlan,
          hasQPP2: !!response.TaxTypeQuebecPensionPlan2,
          hasEI: !!response.TaxTypeEmploymentInsurance,
          hasEHT: !!response.TaxTypeHealthCare
        })
        
        setTaxData(response)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tax data'
        console.error('Error fetching tax data:', errorMessage, { subdivisionId })
        setTaxDataError(errorMessage)
        setTaxData(null)
      } finally {
        setTaxDataLoading(false)
      }
    }

    fetchTaxData()
  }, [subdivisionId, enabled])

  return {
    taxData,
    taxDataLoading,
    taxDataError
  };
} 